/**
 * Purchase entity class
 */

import moment from 'moment'
import { has } from '../../../app/helpers/object'

/**
 * PurchaseFactory
 * returns Purchase entity class
 *
 * @class PurchaseFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Purchase} class
 *
 * @ngdoc factory
 * @name mwApp.factory:PurchaseFactory
 */
/* @ngInject */
export default function PurchaseFactory ($http, alertLiteSvc, Type, Entity) {
  /* === Purchase class === */

  /**
   * Purchase model
   *
   * @class Purchase
   * @classdesc Purchase model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @return {models.Purchase} Purchase entity
   */
  function Purchase () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Purchase.prototype = Object.create(Entity.prototype)
  Purchase.prototype.constructor = Purchase

  /* === Purchase instance methods & fields === */

  Object.assign(Purchase.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Purchase#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      autoPay: Boolean,
      availableFrom: moment,
      availableTo: moment,
      avod: Boolean,
      canBeAdjusted: Boolean,
      cancellationReason: String,
      captureId: String,
      category: String,
      checkNumber: String,
      connectorId: String,
      creditCard: 'CreditCard',
      currency: String,
      description: String,
      details: { paymentMethodType: String },
      discountCampaign: {
        code: String,
        discountAmount: Number,
        name: String,
        type: 'FIXED_AMOUNT',
        value: Number
      },
      discounted: Boolean,
      errorNote: String,
      length: String,
      monetizationType: String,
      paidDevices: Number,
      paymentStatementId: Number,
      periodUnlimited: Boolean,
      pricingRuleApplied: Boolean,
      processDate: moment,
      paymentMethod: String,
      productType: String,
      prorated: Boolean,
      purchaseNumber: String,
      purchaseSetItems: [],
      sourceType: String,
      status: String, // PAYMENT_STATUSES
      subscriptionStatus: String,
      totalAmount: Number,
      transactionId: String
    },

    /**
     * url template
     *
     * @memberof models.Purchase#
     * @type {String}
     */
    _urlTemplate: 'api/subscribers/{{::subscriberId || subscriber.id}}/payments/{{::id}}',

    /**
     * Get main subscription
     *
     * @memberof models.Purchase#
     * @return {models.Subscription}
     */
    _mainSubscription: function () {
      if (this.subscriptions) {
        for (var i = 0; i < this.subscriptions.length; i++) {
          if (this.subscriptions[i].subscriptionType === 'Subscription') {
            return this.subscriptions[i]
          }
        }
      }
    },

    /**
     * Set comment for payment statement
     *
     * @memberof models.Purchase#
     * @param {String} [text] - comment text (if absent, this.comment will be used)
     * @return {Promise} promise
     */
    _comment: function (text) {
      var comment = text || this.comment
      var original = this.comment
      return $http.patch(this._url + '/comment', JSON.stringify(comment))
        .then(
          () => {
            this.comment = comment
          }
        ).catch(
          rejection => {
            this.comment = original
            return alertLiteSvc.alertError(rejection)
          }
        )
    },

    /**
     * Void payment statement
     *
     * @memberof models.Purchase#
     * @return {Promise} promise
     */
    _void: function () {
      var status = this.status
      return $http.patch(this._url + '/void')
        .then(
          () => {
            this.status = 'VOIDED'
          }
        ).catch(
          rejection => {
            this.status = status
            return alertLiteSvc.alertError(rejection)
          }
        )
    },

    /**
     * Approve payment statement
     *
     * @memberof models.Purchase#
     * @return {Promise} promise
     */
    _approve: function () {
      var status = this.status
      return $http.patch(this._url + '/approve')
        .catch(
          rejection => {
            this.status = status
            return alertLiteSvc.alertError(rejection)
          }
        )
    }

  })

  /* === Purchase static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Purchase, Entity)
  Purchase._name = 'Purchase'
  Purchase.init()

  // permissions
  Purchase.access = {
    view: 'viewSubscriptionPayment',
    create: 'createSubscriptionPayment'
  }

  /**
   * Get purchases
   *
   * @memberof models.Purchase#
   */
  Purchase.getPurchases = function (tableState, subscriberId) {
    var params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number || 25
    }
    Object.assign(params, tableState.search.predicateObject)

    if (params.processDate) {
      params['process-date-from'] = JSON.parse(params.processDate).after
      params['process-date-to'] = JSON.parse(params.processDate).before
      delete params.processDate
    }

    if (params.subscriptionStatuses) {
      params['subscription-statuses'] = params.subscriptionStatuses
      delete params.subscriptionStatuses
    }

    if (params.purchaseStatus) {
      params['purchase-status'] = params.purchaseStatus
      delete params.purchaseStatus
    }

    if (params.purchaseNumber) {
      params['purchase-number'] = params.purchaseNumber
      delete params.purchaseNumber
    }

    if (params.productTypes) {
      params['product-types'] = params.productTypes
      delete params.productTypes
    }

    const url = `/nora/api/subscribers/${subscriberId}/purchases/items`

    return $http.get(url, { params })
      .data()
      .then((response) => {
        const data = has('payload')(response)
          ? response.payload
          : response

        var result = this.cast(data, {
          // response structure
          content: ['Purchase'],
          first: true,
          last: true,
          number: 0,
          totalPages: 0,
          totalElements: 0,
          size: 0,
          sort: [{
            ascending: true,
            direction: '',
            ignoreCase: true,
            nullHandling: '',
            property: ''
          }]
        })

        if (Array.isArray(result.sort) && result.sort.length) {
          var last = result.sort.pop()
          tableState.sort.predicate = last.property
          tableState.sort.reverse = !last.ascending
        }
        tableState.pagination.start = result.number * result.size // first element
        tableState.pagination.number = result.size // page size
        tableState.pagination.numberOfPages = result.totalPages
        tableState.pagination.totalItemCount = result.totalElements
        return result
      })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  return Purchase
}
